import { Box, Divider, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import ResetButton from '~/components/Buttons/ResetButton';
import { Slider } from '~/components/Slider';
import {
    domainEventBus,
    StructureSetContourThicknessChangeRequestedEvent,
} from '~/domain/events';
import {
    defaultGlobalROIContourThickness,
    globalROIContourThicknessState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

const GlobalContourThicknessModifier = () => {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const [globalROIContourThickness, setGlobalROIContourThickness] =
        useRecoilState(globalROIContourThicknessState);

    const handleThicknessChange = (newValue: number | number[]) => {
        const thickness = newValue as number;
        debouncedSetThickness(thickness);
    };

    const debouncedSetThickness = useMemo(
        () =>
            debounce((thickness: number) => {
                setGlobalROIContourThickness(thickness);
                if (currentStructureSet?.dicomUid) {
                    domainEventBus.publish(
                        new StructureSetContourThicknessChangeRequestedEvent(
                            currentStructureSet.dicomUid,
                            thickness
                        )
                    );
                }
            }, 300),
        [setGlobalROIContourThickness, currentStructureSet?.dicomUid]
    );

    const handleReset = () => {
        setGlobalROIContourThickness(defaultGlobalROIContourThickness);
        if (currentStructureSet?.dicomUid) {
            domainEventBus.publish(
                new StructureSetContourThicknessChangeRequestedEvent(
                    currentStructureSet.dicomUid,
                    defaultGlobalROIContourThickness
                )
            );
        }
    };

    return (
        <Stack display="flex" flexDirection="column" gap={1}>
            <Slider
                label={
                    <Box display={'flex'} alignItems={'center'}>
                        <Typography fontSize={13}>
                            <FormattedMessage id="contourThickness" />
                        </Typography>
                        <Divider
                            flexItem
                            orientation="vertical"
                            sx={{ mx: 1, my: 0.5 }}
                        />
                        <ResetButton
                            onClick={handleReset}
                            disabled={!currentStructureSet}
                        />
                    </Box>
                }
                value={globalROIContourThickness}
                step={0.1}
                min={0.5}
                max={2}
                onChange={handleThicknessChange}
                disabled={!currentStructureSet}
                testId="contour-thickness-slider"
            />
        </Stack>
    );
};

export default GlobalContourThicknessModifier;
